import React, { useEffect, useState, lazy, Suspense } from "react";
import { getPosts } from "../utils/networkApi";
import Loader from "../components/Loader";
import { useTranslation } from "react-i18next";
import { updateMetaTags } from "../utils/seo";

// 🔥 Lazy Load Components (Performance Boost)
const JobHighlights = lazy(() => import("../components/JobHighlights"));
const Announcements = lazy(() => import("../components/Announcements"));
const NoticeBoard = lazy(() => import("../components/NoticeBoard"));
const Editorial = lazy(() => import("../components/Editorial"));
const SocialMediaSection = lazy(() => import("../components/SocialMediaSection"));
const LogoCarousel = lazy(() => import("../components/LogoCarousel"));

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  // ✅ Single State (Less Re-render)
  const [data, setData] = useState({
    announcements: [],
    jobHighlight: [],
    slider: [],
    sliderTime: [],
    advertis: [],
    articleLead: [],
    articleCareer: [],
    articleSpecial: [],
    articleSuccess: [],
    footerLogo: [],
    footerUrl: [],
    social: []
  });

  // ✅ SEO Meta Tags
  useEffect(() => {
    updateMetaTags(
      `${t("home")} | ${t("site-title")}`,
      t("home-desc"),
      t("home-keywords")
    );
  }, [t, i18n.language]);

  // ✅ Fetch Data (Optimized)
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);

        const savedLang = localStorage.getItem("appLang") || "en";
        const cacheKey = `homeData_${savedLang}`;

        // 🔥 Check Cache First
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          setData(JSON.parse(cached));
          // setLoading(false);
          // return; // Commented out to ensure API calls are made
        }

        // 🔥 Parallel API Calls
        const [
          homeRes,
          leadRes,
          careerRes,
          specialRes,
          successRes
        ] = await Promise.all([
          getPosts(`home-api?lang=${savedLang}`),
          getPosts(`article-api?lang=${savedLang}&type=lead`),
          getPosts(`article-api?lang=${savedLang}&type=career`),
          getPosts(`article-api?lang=${savedLang}&type=special`),
          getPosts(`article-api?lang=${savedLang}&type=success`)
        ]);

        const finalData = {
          announcements: homeRes.announcement?.data || [],
          jobHighlight: homeRes.jobHighlight || [],
          slider: homeRes.slider || [],
          sliderTime: homeRes.sliderTime || [],
          advertis: homeRes.advertisement || [],
          articleLead: leadRes.data || [],
          articleCareer: careerRes.data || [],
          articleSpecial: specialRes.data || [],
          articleSuccess: successRes.data || [],
          footerLogo: homeRes.footerLogo || [],
          footerUrl: homeRes.footerUrl || [],
          social: homeRes.social || []
        };

        // ✅ Set State Once
        setData(finalData);

        // ✅ Save to Cache
        localStorage.setItem(cacheKey, JSON.stringify(finalData));

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  return (
    <div>
      {/* 🔥 Show Top Section Immediately */}
      <Suspense fallback={<Loader />}>
        <JobHighlights
          jobHighlight={data.jobHighlight}
          carouselImages={data.slider}
          sliderTime={data.sliderTime}
        />
      </Suspense>

      {/* 🔥 Load Rest Gradually */}
      <Suspense fallback={<Loader />}>
        {!loading && (
          <>
            <Announcements announcements={data.announcements} />
            <NoticeBoard advertis={data.advertis} />

            <Editorial
              leadArticles={data.articleLead}
              careerArticles={data.articleCareer}
              specialArticles={data.articleSpecial}
              successArticles={data.articleSuccess}
            />

            <SocialMediaSection data={data.social} />

            <LogoCarousel
              logos={data.footerLogo}
              footerUrl={data.footerUrl}
            />
          </>
        )}
      </Suspense>

      {/* 🔥 Optional Loader */}
      {loading && <Loader />}
    </div>
  );
};

export default HomePage;